import axios from 'axios';
import { workTypeMeta } from './workTypes';
import { queryClient } from '../lib/queryClient';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_BASE_URL
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Any successful write (create, update, delete) makes cached lists out of date.
// Mark them stale; pages that are on screen refetch automatically, the rest
// refetch the next time they are opened. No page has to remember to reload.
api.interceptors.response.use(
  (response) => {
    const method = (response.config?.method || 'get').toLowerCase();
    if (method !== 'get') {
      queryClient.invalidateQueries();
    }
    return response;
  },
  (error) => {
    // The token expired or was revoked: drop it and tell the app to sign out.
    if (error.response?.status === 401 && localStorage.getItem('token')) {
      localStorage.removeItem('token');
      queryClient.clear();
      window.dispatchEvent(new Event('auth:expired'));
    }
    return Promise.reject(error);
  }
);

export default api;

// Helper functions
export const getStatusColor = (status) => {
  const colors = {
    event_created: 'bg-[#94A3B8]',
    event_scheduled: 'bg-[#3B82F6]',
    shoot_completed: 'bg-[#F59E0B]',
    delivery_in_progress: 'bg-[#8B5CF6]',
    closed: 'bg-[#10B981]',
    assigned: 'bg-[#94A3B8]',
    in_progress: 'bg-[#F59E0B]',
    completed: 'bg-[#10B981]'
  };
  return colors[status] || 'bg-slate-500'};

export const getPriorityColor = (priority) => {
  const colors = {
    normal: 'bg-[#64748B]',
    high: 'bg-[#F59E0B]',
    vip: 'bg-[#EF4444]'
  };
  return colors[priority] || 'bg-slate-500';
};

export const getTaskTypeColor = (type) => workTypeMeta(type).badge;

export const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatTime = (date) =>
  date ? new Date(date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

export const relativeDay = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return diff > 0 ? `In ${diff} days` : `${-diff} days ago`;
};
